<template>
  <section class="section">
    <div class="container">
      <Header v-bind="$route.meta" />
      <b-loading v-model="isLoading" :is-full-page="false" />

      <b-message v-if="!isLoading && !rules.length" type="is-info" has-icon>
        {{ $t('pages.rules.empty') }}
      </b-message>

      <div v-else>
        <ag-grid-vue
          class="ag-theme-alpine"
          style="height: 600px"
          :modules="modules"
          :pagination="true"
          :pagination-auto-page-size="true"
          :grid-options="gridOptions"
          :row-data="rules"
          :column-defs="columnDefs"
          :get-context-menu-items="getContextMenuItems"
        />
      </div>
    </div>

    <b-modal v-model="editModalOpen" :width="620" scroll="keep">
      <div class="card rules-card">
        <header class="rules-card__header">
          <p class="rules-card__eyebrow">
            {{ $t('pages.rules.edit') }}
          </p>
          <h2 class="rules-card__title">
            {{ editForm.code }}
          </h2>
          <p v-if="editForm.description" class="rules-card__subtitle">
            {{ editForm.description }}
          </p>
        </header>

        <div class="rules-card__body">
          <section class="rules-section rules-section--row">
            <span class="rules-section__label">{{ $t('pages.rules.grid.enabled') }}</span>
            <b-switch v-model="editForm.enabled" />
          </section>

          <hr class="rules-divider">

          <section class="rules-section">
            <span class="rules-section__label">{{ $t('pages.rules.grid.scopeValue') }}</span>
            <div class="rules-tokens">
              <b-tag
                v-for="(code, i) in editForm.countries"
                :key="code"
                type="is-info"
                size="is-medium"
                closable
                :title="countryName(code)"
                @close="removeCountry(i)"
              >
                {{ code }}
              </b-tag>
              <span v-if="!editForm.countries.length" class="rules-tokens__empty">
                {{ $t('pages.rules.noCountries') }}
              </span>
            </div>
            <b-field
              grouped
              :type="countryError ? 'is-danger' : ''"
              :message="countryError"
            >
              <b-input
                v-model="countryInput"
                expanded
                :placeholder="$t('pages.rules.countryPlaceholder')"
                @keyup.native.enter="addCountry"
                @input="countryError = ''"
              />
              <p class="control">
                <b-button icon-left="plus" @click="addCountry">
                  {{ $t('pages.rules.addCountry') }}
                </b-button>
              </p>
            </b-field>
          </section>

          <hr class="rules-divider">

          <section class="rules-section">
            <span class="rules-section__label">{{ $t('pages.rules.grid.requirement') }}</span>
            <div class="buttons rules-toggles">
              <b-checkbox-button
                v-for="option in requirementOptions"
                :key="option"
                v-model="editForm.requirements"
                :native-value="option"
                type="is-primary"
              >
                {{ option }}
              </b-checkbox-button>
            </div>
          </section>
        </div>

        <footer class="rules-card__footer">
          <b-button @click="editModalOpen = false">
            {{ $t('global.cancel') }}
          </b-button>
          <b-button type="is-primary" :loading="isSaving" @click="submitEdit">
            {{ $t('global.save') }}
          </b-button>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
import Plant from '@/services/v2/plant'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import { countries } from '@/views/GpsViews/Admin/customerForecast/countries'

const COUNTRY_NAME_BY_CODE = countries.reduce((acc, c) => {
  acc[c.code] = c.name
  return acc
}, {})

export default {
  name: 'PlantRules',
  data () {
    return {
      modules: AllModules,
      isLoading: false,
      isSaving: false,
      rules: [],
      requirementOptions: ['TRFO', 'TRFI', 'DEL', 'COL'],
      editModalOpen: false,
      countryInput: '',
      countryError: '',
      editForm: {
        ruleId: null,
        code: '',
        description: '',
        enabled: false,
        countries: [],
        requirements: []
      },
      gridOptions: {
        defaultColDef: {
          sortable: true,
          resizable: true,
          filter: true
        }
      },
      columnDefs: [
        { headerName: this.$t('pages.rules.grid.code'), field: 'code', minWidth: 160 },
        { headerName: this.$t('pages.rules.grid.description'), field: 'description', flex: 1, minWidth: 240 },
        { headerName: this.$t('pages.rules.grid.scope'), field: 'scope', minWidth: 140 },
        { headerName: this.$t('pages.rules.grid.scopeValue'), field: 'scopeValue', minWidth: 180, valueFormatter: this.listFormatter },
        { headerName: this.$t('pages.rules.grid.enabled'), field: 'enabled', minWidth: 120 },
        { headerName: this.$t('pages.rules.grid.requirement'), field: 'requirement', minWidth: 180, valueFormatter: this.listFormatter }
      ]
    }
  },
  async mounted () {
    await this.loadData()
  },
  methods: {
    async loadData () {
      this.isLoading = true
      try {
        this.rules = await Plant.getRules(this.$route.params.plant_id)
      } finally {
        this.isLoading = false
      }
    },
    listFormatter (params) {
      const value = params.value
      if (value === null || value === undefined) return ''
      if (Array.isArray(value)) return value.join(', ')
      return typeof value === 'object' ? JSON.stringify(value) : value
    },
    countryName (code) {
      return COUNTRY_NAME_BY_CODE[code] || code
    },
    getContextMenuItems (params) {
      const customMenu = []
      if (params.node && params.node.data) {
        customMenu.push({
          name: this.$t('pages.rules.edit'),
          action: () => this.openEdit(params.node.data),
          icon: '<i class="fas fa-fw fa-pen"></i>'
        })
      }
      return [...customMenu, 'separator', 'copy', 'export']
    },
    openEdit (row) {
      this.countryInput = ''
      this.countryError = ''
      this.editForm = {
        ruleId: row.ruleId,
        code: row.code,
        description: row.description,
        enabled: !!row.enabled,
        countries: Array.isArray(row.scopeValue) ? [...row.scopeValue] : [],
        requirements: Array.isArray(row.requirement) ? [...row.requirement] : []
      }
      this.editModalOpen = true
    },
    addCountry () {
      const code = (this.countryInput || '').trim().toUpperCase()
      if (!code) return
      if (!COUNTRY_NAME_BY_CODE[code]) {
        this.countryError = this.$t('pages.rules.invalidCountry', { code })
        return
      }
      if (!this.editForm.countries.includes(code)) {
        this.editForm.countries.push(code)
      }
      this.countryInput = ''
      this.countryError = ''
    },
    removeCountry (index) {
      this.editForm.countries.splice(index, 1)
    },
    async submitEdit () {
      this.isSaving = true
      try {
        await Plant.updateRule(this.$route.params.plant_id, this.editForm.ruleId, {
          enabled: this.editForm.enabled,
          scopeValue: this.editForm.countries,
          requirement: this.editForm.requirements
        })
        this.editModalOpen = false
        this.$buefy.toast.open({ type: 'is-primary', message: this.$t('pages.rules.updated'), duration: 3000 })
        await this.loadData()
      } catch (error) {
        this.$buefy.toast.open({ type: 'is-danger', message: error.message || error, duration: 4000 })
      } finally {
        this.isSaving = false
      }
    }
  }
}
</script>

<style scoped>
.rules-card {
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.22);
}

.rules-card__header {
  padding: 1.75rem 2rem 1.35rem;
  border-bottom: 1px solid #eef0f4;
}

.rules-card__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.09em;
  font-size: 0.7rem;
  font-weight: 600;
  color: #9aa0ad;
  margin-bottom: 0.3rem;
}

.rules-card__title {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.15;
  color: #1f2533;
}

.rules-card__subtitle {
  margin-top: 0.45rem;
  font-size: 0.95rem;
  color: #6b7280;
}

.rules-card__body {
  padding: 1.75rem 2rem;
}

.rules-section {
  margin: 0;
}

.rules-section--row {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.rules-section__label {
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-size: 0.72rem;
  font-weight: 600;
  color: #8a90a0;
  margin-bottom: 0.75rem;
}

.rules-section--row .rules-section__label {
  margin-bottom: 0;
}

.rules-divider {
  height: 1px;
  background: #eef0f4;
  margin: 1.5rem 0;
}

.rules-tokens {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-height: 3rem;
  padding: 0.75rem;
  margin-bottom: 0.85rem;
  border: 1px solid #e6e8ef;
  border-radius: 10px;
  background: #fafbfc;
}

.rules-tokens__empty {
  color: #aab0bd;
  font-size: 0.875rem;
  font-style: italic;
}

.rules-toggles {
  gap: 0.5rem;
  margin-bottom: 0;
}

.rules-card__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1.25rem 2rem;
  border-top: 1px solid #eef0f4;
  background: #fafbfc;
}
</style>
